import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js';
import { formatCurrency } from "./CoreFunctions";

function GroupedBarChart({ data }) {
  // Chart refs
  const mainChartRef = useRef(null);
  const topChartRef = useRef(null);
  const mainCanvasRef = useRef(null);
  const topCanvasRef = useRef(null);

  // Data filtering based on date range
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState({start: null, end: null});

  const persistActiveRange = (val) => {
    try { window.localStorage.setItem('chart.activeRange', String(val)); } catch (e) {}
  };
  const getInitialActiveRange = () => {
    try {
      const val = window.localStorage.getItem('chart.activeRange');
      if (val) return val;
      return 'all'; // Default to all if no preference saved
    } catch (e) { return 'all'; }
  };

  const handleRangeChange = (monthsOrYTD) => {
    console.log('GroupedRevolverBarChart2: handleRangeChange', monthsOrYTD);
    if (!data || data.length === 0) return;
    const sorted = [...data].sort((a, b) => {
      const da = new Date(a.period);
      const db = new Date(b.period);
      return da - db;
    });
    const latest = new Date(sorted[sorted.length - 1].period);
    let start;
    let end = latest;
    if (monthsOrYTD === 'YTD') {
      const now = new Date();
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31); // end of current year
    } else {
      start = new Date(latest);
      start.setMonth(latest.getMonth() - (monthsOrYTD - 1));
    }
    setDateRange({ start, end });
    persistActiveRange(monthsOrYTD);
  };

  // Initialize date range based on persisted selection or ALL by default
  useEffect(() => {
    if (!data || data.length === 0) return;
    const sorted = [...data].sort((a, b) => new Date(a.period) - new Date(b.period));
    const latest = new Date(sorted[sorted.length - 1].period);
    const sel = getInitialActiveRange();
    console.log('GroupedRevolverBarChart2: Initializing range, sel=', sel, 'latest=', latest);
    if (sel === 'all') {
      console.log('GroupedRevolverBarChart2: Setting All range');
      setDateRange({ start: null, end: null });
    } else if (sel === 'YTD') {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      console.log('GroupedRevolverBarChart2: Setting YTD range', start, end);
      setDateRange({ start, end });
    } else {
      // Assume number of months
      let months = 12;
      if (sel === '3months') months = 3; 
      if (sel && !isNaN(sel)) months = parseInt(sel);
      const start = new Date(latest);
      start.setMonth(latest.getMonth() - (months - 1));
      start.setDate(1);
      console.log('GroupedRevolverBarChart2: Setting months range', start, latest);
      setDateRange({ start, end: latest });
    }
  }, [data]);

  // Filter data when dateRange changes
  useEffect(() => {
    console.log('GroupedRevolverBarChart2: Filtering data, data.length=', data?.length, 'dateRange=', dateRange);
    if (!data || data.length === 0) {
      setFilteredData([]);
      return;
    }
    if (!dateRange.start && !dateRange.end) {
      setFilteredData(data);
      return;
    }
    const {start, end} = dateRange;
    const filtered = data.filter(item => {
      const dateStr = item.period;
      const itemDate = new Date(dateStr + ' 1');
      const afterStart = !start || itemDate >= start;
      const beforeEnd = !end || itemDate <= end;
      return afterStart && beforeEnd;
    });
    console.log('GroupedRevolverBarChart2: Filtered result length=', filtered.length);
    setFilteredData(filtered);
  }, [data, dateRange]);

  // --- Initialize the top chart (interest + interest rate) once ---
  useEffect(() => {
    if (!topChartRef.current && data) {
      const labels = [];
      topChartRef.current = new Chart(topCanvasRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'AvgRate (%)',
              data: [],
              borderColor: 'rgba(54, 162, 235, 1)',
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              yAxisID: 'yRate',
              type: 'line',
              fill: false,
              tension: 0.4,
            },
            {
              label: 'Int.Pmt. ($)',
              data: [],
              backgroundColor: 'rgba(255, 159, 64, 0.6)',
              yAxisID: 'yAmount',
              type: 'bar',
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {legend: {position: 'right', labels: { color: '#e5e7eb' }} },
          scales: {
            yAmount: {
              type: 'linear',
              position: 'left',
              beginAtZero: true,
              title: {display: true, text: 'Interest Payment ($)'},
            },
            yRate: {
              type: 'linear',
              position: 'right',
              beginAtZero: true,
              title: {display: true, text: 'Interest Rate (%)'},
              grid: {drawOnChartArea: false},
            },
            x: {
              ticks: {
                display: false,
              }
            }
          },
        },
      });
    }
  }, []);

  // --- Update top chart data whenever filteredData changes ---
  useEffect(() => {
    if (!filteredData || filteredData.length === 0 || !topChartRef.current) {
      console.log('GroupedRevolverBarChart2: Skipping top chart update, filteredData.length=', filteredData?.length);
      return;
    }
    const sorted = [...filteredData].sort((a, b) => new Date(a.period) - new Date(b.period));
    const labels = sorted.map(it => it.period);
    const interestPayments = sorted.map(it => it.interest_charged ?? 0);
    const interestRates = sorted.map(it => it.weighted_interest_rate * 100 ?? 0);

    const chart = topChartRef.current;
    chart.data.labels = labels;
    chart.data.datasets[1].data = interestPayments;
    chart.data.datasets[0].data = interestRates;
    chart.update();
  }, [filteredData]);

  // --- Initialize main chart once ---
  useEffect(() => {
    console.log('GroupedRevolverBarChart2: Main chart useEffect, filteredData.length=', filteredData?.length)
    if (filteredData && filteredData.length > 0) {
      // assemble data arrays
      const sorted = [...filteredData].sort((a, b) => new Date(a.period) - new Date(b.period));
      const labels = sorted.map(it => it.period);
      const beg_balance = sorted.map(it => it.previous_balance ?? 0);
      const interest = sorted.map(it => it.interest_charged ?? 0);
      const transactions = sorted.map(it => it.transactions ?? 0);
      const payments = sorted.map(it => (it.payments ?? 0) + (it.other_credits ?? 0));
      const balance_int = sorted.map(it => it.balance_subject_to_interest_rate ?? 0);
      const end_balance = sorted.map(it => it.new_balance ?? 0);
      const breakdowns = sorted.map(it => it.breakdown ?? {});

      console.log('Labels:', labels);
      console.log('Beg Balance:', beg_balance);
      console.log('Interest:', interest);
      // ... more logs as needed

      if (!mainChartRef.current) {
        // initialize chart
        mainChartRef.current = new Chart(mainCanvasRef.current, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { label: 'Beginning Balance', data: beg_balance, backgroundColor: 'rgba(120, 170, 92, 0.6)' },
              { label: 'Transactions', data: transactions, backgroundColor: 'rgba(75, 192, 192, 0.6)' },
              // { label: 'Interest', data: interest, backgroundColor: 'rgba(255, 159, 64, 0.6)' },
              { label: 'Payments + Credits', data: payments, backgroundColor: 'rgba(153, 102, 255, 0.6)' },
              { label: 'Balance Subject to IR', data: balance_int, backgroundColor: 'rgba(110, 200, 92, 0.6)' },
              { label: 'End Balance', data: end_balance, backgroundColor: 'rgba(100, 125, 92, 0.6)' },
            ],
          },
          options: {
            responsive: true,
            plugins: {
              tooltip: {
                usePointStyle: true,
                bodyFont: {
                  family: 'monospace',
                },
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    const amountStr = formatCurrency(value, 0);
                    const labelWidth = 28; // Reduced slightly to account for point style spacing if needed
                    return `${label.padEnd(labelWidth)}${amountStr.padStart(12)}`;
                  },
                  afterBody: (context) => {
                    const dataIndex = context[0].dataIndex;
                    const breakdown = breakdowns[dataIndex];
                    if (!breakdown) return '';

                    const lines = [''];
                    const labelWidth = 30; // Matches total width of (point + label) if possible, or just keep it consistent
                    Object.entries(breakdown).forEach(([type, items]) => {
                      const typeTotal = Object.values(items).reduce((a, b) => a + b, 0);
                      const displayType = type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                      const typeTotalStr = formatCurrency(typeTotal, 0);
                      const typeHeader = `${displayType}:`;
                      // For afterBody, we add 2 spaces to align with the text in label (which follows the point)
                      lines.push(`  ${typeHeader.padEnd(28)}${typeTotalStr.padStart(12)}`);

                      Object.entries(items).forEach(([name, amount]) => {
                        const amountStr = formatCurrency(amount, 0);
                        const nameLabel = `  - ${name}:`;
                        lines.push(`  ${nameLabel.padEnd(28)}${amountStr.padStart(12)}`);
                      });
                    });
                    return lines;
                  }
                }
              },
              legend: {
                onClick: (e, legendItem, legend) => {
                  const index = legendItem.datasetIndex;
                  const chart = legend.chart;
                  const meta = chart.getDatasetMeta(index);
                  meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                  chart.update();
                },
                position: 'right',
              },
            },
            scales: {
              x: { stacked: false, ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
              y: { beginAtZero: true, stacked: false, ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
            },
          },
        });
      } else {
        // update datasets
        const chart = mainChartRef.current;
        chart.data.labels = labels;
        chart.data.datasets[0].data = beg_balance;
        chart.data.datasets[1].data = transactions;
        // chart.data.datasets[2].data = interest;
        chart.data.datasets[2].data = payments;
        chart.data.datasets[3].data = balance_int;
        chart.data.datasets[4].data = end_balance;

        // Update tooltip breakdown reference
        chart.options.plugins.tooltip.usePointStyle = true;
        chart.options.plugins.tooltip.bodyFont = { family: 'monospace' };
        chart.options.plugins.tooltip.callbacks.label = (context) => {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          const amountStr = formatCurrency(value, 0);
          const labelWidth = 28;
          return `${label.padEnd(labelWidth)}${amountStr.padStart(12)}`;
        };
        chart.options.plugins.tooltip.callbacks.afterBody = (context) => {
          const dataIndex = context[0].dataIndex;
          const breakdown = breakdowns[dataIndex];
          if (!breakdown) return '';

          const lines = [''];
          const labelWidth = 30; // Fixed width for label part
          Object.entries(breakdown).forEach(([type, items]) => {
            const typeTotal = Object.values(items).reduce((a, b) => a + b, 0);
            const displayType = type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
            const typeTotalStr = formatCurrency(typeTotal, 0);
            const typeHeader = `${displayType}:`;
            lines.push(`  ${typeHeader.padEnd(28)}${typeTotalStr.padStart(12)}`);

            Object.entries(items).forEach(([name, amount]) => {
              const amountStr = formatCurrency(amount, 0);
              const nameLabel = `  - ${name}:`;
              lines.push(`  ${nameLabel.padEnd(28)}${amountStr.padStart(12)}`);
            });
          });
          return lines;
        };

        chart.update();
      }
    }
  }, [filteredData]);

  return (
      <div style={{display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
        {/* Date Range Buttons */}
        <div style={{marginBottom: '1em'}}>
          <button onClick={() => handleRangeChange(12)}>Last 12 Months</button>
          <button onClick={() => handleRangeChange(3)}>Last 3 Months</button>
          <button onClick={() => handleRangeChange('YTD')}>YTD</button>
          <button onClick={() => {
            console.log('GroupedRevolverBarChart2: Show All clicked');
            setDateRange({start: null, end: null});
            persistActiveRange('all');
          }}>Show All</button>
        </div>

        {/* Top Chart: Interest Payments & Interest Rate Line */}
        <div id="chartsContainer" style={{width: '100%', display: "flex", "flex-direction": "column"}}>
          <canvas ref={topCanvasRef} height={150} style={{ width: '100%', minHeight: '150px' }} />
          <canvas ref={mainCanvasRef} height={500} style={{ width: '100%', minHeight: '500px' }}/>
        </div>

        {/*/!* Top Chart: Interest Payments & Interest Rate Line *!/*/}
        {/*<div style={{height: '25%', minHeight: '150px'}}>*/}
        {/*  <canvas ref={topCanvasRef} height={150} style={{ width: '100%', height: '100%' }} />*/}
        {/*</div>*/}

        {/*/!* Main Chart: all balance/transaction info *!/*/}
        {/*<div style={{height: '70%', minHeight: '500px'}}>*/}
        {/*  <canvas ref={mainCanvasRef} height={500} style={{ width: '100%', height: '100%' }}/>*/}
        {/*</div>*/}


      </div>
  );
}

export default GroupedBarChart;